// src/utils/accountWatch.js
// Tell a switched-off person their session is over (punch list line 38,
// 2026-09-23).
//
// Delete / Disable in User Management only set users.type = 1 / 2; the Firebase
// session lives on. App.js checks the type once, at launch, and never again, so
// a tab left open kept looking normal while, since the 09-23 rules lockdown,
// every save it made was quietly refused: a dragged card stayed where it was
// dropped and snapped back on the next reload. Worst case was the open house
// iPad, which swallowed every guest's sign-in into its retry queue.
//
// This watches the signed-in person's OWN users record, the one read the rules
// still allow a switched-off login (firestore.rules, the users read arm), and
// hands App.js the same message its launch-time check already shows, once.
// A failed read never ends a session: the rules are the fence; this is the
// courtesy of saying so.
import { doc, onSnapshot } from "firebase/firestore";
import { firestored } from "../firebase/firebaseConfig";

export const ACCOUNT_DELETED_MESSAGE = "Your account has been deleted. Kindly contact Administrator";
export const ACCOUNT_DISABLED_MESSAGE = "Your account has been disabled. Kindly contact Administrator";

/**
 * The message for a record that is switched off, or null while it is fine.
 * A MISSING record is "fine" here on purpose: Delete in User Management sets
 * type 1, it never removes the record, and Firestore can report a document as
 * absent from a cold cache for a moment before the real read lands. Signing
 * someone out on that moment would empty a working session on a bad signal.
 */
export function accountEndedMessage(userDoc) {
  if (!userDoc) return null;
  const type = Number(userDoc.type);
  if (type === 1) return ACCOUNT_DELETED_MESSAGE;
  if (type === 2) return ACCOUNT_DISABLED_MESSAGE;
  return null;
}

/**
 * Watch users/{uid}. Calls onEnded(message) once, the first time the record is
 * switched off or gone, then stops watching. Returns the unsubscribe.
 */
export function watchOwnAccount(uid, onEnded) {
  const id = String(uid || "").trim();
  if (!id) return () => {};
  let ended = false;
  let stop = () => {};
  try {
    stop = onSnapshot(
      doc(firestored, "users", id),
      (snap) => {
        if (ended) return;
        const message = accountEndedMessage(snap.exists() ? snap.data() : null);
        if (!message) return;
        ended = true;
        stop();
        onEnded(message);
      },
      (err) => {
        console.error("own account watch failed:", err);
      },
    );
  } catch (e) {
    console.error("own account watch could not start:", e);
    return () => {};
  }
  return () => {
    ended = true;
    stop();
  };
}
